import { useState } from "react";
import { api, ApiError, type CasoDetalle } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  casoId: number;
  onListo: (caso: CasoDetalle) => void;
};

/**
 * «Ya lo vi» sobre la respuesta del proveedor.
 *
 * La ficha «el proveedor contestó» solo se apagaba respondiéndole en su ticket.
 * Cuando el caso se resuelve por otra vía (garantía, reemplazo, el cliente
 * desistió) la ficha se quedaba encendida para siempre y el tablero mentía.
 *
 * Pide un motivo OPCIONAL a propósito: no estorba al que solo quiere quitarse
 * la ficha, y al que sí escribe le deja en la bitácora la respuesta a «¿por qué
 * nunca le contestamos al proveedor?».
 */
export default function ProveedorVistoModal({ open, onOpenChange, casoId, onListo }: Props) {
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const toast = useToast();

  async function confirmar() {
    setGuardando(true);
    try {
      const actualizado = await api.post<CasoDetalle>(`/api/casos/${casoId}/proveedor-visto`, {
        motivo: motivo.trim() || null,
      });
      onListo(actualizado);
      toast.success("Marcado como atendido", "Queda registrado en la bitácora");
      setMotivo("");
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo marcar", msg);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Dar por atendida la respuesta del proveedor"
      size="sm"
      footer={
        <>
          <button onClick={() => onOpenChange(false)} className="btn-ghost">
            Cancelar
          </button>
          <button onClick={confirmar} disabled={guardando} className="btn-primary">
            {guardando ? "Guardando…" : "Ya lo vi"}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-zinc-400">
          Se quita la ficha <strong className="text-zinc-200">«el proveedor contestó»</strong> y el
          caso sale del tablero de pendientes. Úsalo cuando ya no hace falta responderle en su
          ticket — por ejemplo si el caso se resolvió por garantía o reemplazo.
        </p>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-zinc-500">
            Motivo (opcional)
          </span>
          <input
            className="input mt-1 w-full"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !guardando) void confirmar();
            }}
            placeholder="Ej. se resolvió por garantía"
            maxLength={200}
            autoFocus
          />
        </label>
        <p className="text-[11px] text-zinc-500">
          Si el proveedor vuelve a escribir, la ficha reaparece sola.
        </p>
      </div>
    </Modal>
  );
}
